import createModule from "../createModule"
import view from "./view"
import { chain, get, includes, toLower } from "lodash"
import { Creators as PortfolioRootCreators } from "../portfolio-root/model"
import { getProfilePeriod } from "modules/portfolio-root/selectors/dates"
import { Creators as notificationsCreators } from "modules/notifications/model"
import { getSecurity, getSecurityWatchlists, getWatchlistsMenuVisible, getBase, getBaseName, getRawSecurityId, getMenuItems, getDisplayCompositeLink, getAnalyzeCompositeAvailable, getCurrency, getUnsupportedCurrencyWarning } from "./selectors"
import { getIsIntradayAnalyticsEnabled } from "selectors/features"
import { getSelectedDate, getEarliestDate, getLatestDate, getStartDate, getEndDate, getDatesAdjusted } from "./selectors/dates"
import { getFileName } from "./selectors/file"
import { Creators } from "./model"
import { getMissingDataFiles } from "selectors/missingDataFiles"
import { classificationsQuery, getClassifications } from "helpers/classification"
import { MODEL_METADATA_QUERY } from "resources/models"
import { CONTENT_SETS_METADATA_QUERY } from "resources/content-sets"
import { FORECAST_LIST_QUERY } from "resources/forecasts"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: state.global.selected.account,
    accountId: state.global.selected.accountId,
    startDate: getStartDate(state, props),
    endDate: getEndDate(state, props),
    latestDate: getLatestDate(state, props),
    base: state.modules.securityProfile.base,
    classifications: getClassifications(state),
  }),
  required: ["account", "modelId", "startDate"],
  data: [{
    resource: "dates",
    as: null,
    url: ({ modelId }) => {
      if (!modelId) {
        return null
      }

      return {
        id: modelId,
        query: "query ($modelId: String!) { model (id: $modelId) { availability { currentDate, factorsStartDate, securitiesStartDate } } }",
        variables: { modelId },
      }
    },
  }, {
    resource: "securityAvailability",
    url: ({ modelId, security }) => {
      if (security) {
        const { exchange, ticker } = security
        return {
          id: `securityAvailability-${modelId}${ticker}${exchange}`,
          query: `query ($modelId: String!, $ticker: String!, $mic: String!) {
            model (id: $modelId) {
              security (ticker: $ticker, mic: $mic) {
                availability {
                  from
                  to
                }
              }
            }
          }`,
          variables: {
            modelId,
            ticker,
            mic: exchange,
          },
        }
      } else {
        return null
      }
    },
  }, {
    resource: "baseSecurityAvailability",
    url: ({ modelId, base }) => {
      if (base && includes(["security", "index"], toLower(base.type))) {
        const { mic, ticker, id: indexId } = base
        return {
          id: `securityAvailability-${modelId}${mic}${ticker}${indexId}`,
          query: `query ($modelId: String!, $ticker: String, $mic: String, $indexId: String) {
            model (id: $modelId) {
              security (ticker: $ticker, mic: $mic, indexId: $indexId) {
                availability {
                  from
                  to
                }
              }
            }
          }`,
          variables: {
            modelId,
            ticker,
            mic,
            indexId,
          },
        }
      } else {
        return null
      }
    },
  }, {
    resource: "securityDescriptors",
    url: ({ modelId, security: { exchange, ticker }, latestDate, classifications }) => {
      if (latestDate) {
        // GICS is multitier, we want the INDUSTRY one specifically. PSC are single tier, there's no need to add it in the query
        const classificationsQueries = chain(classifications)
          .map(c => `${c.id}:classification(id:"${c.id}" on:$on ${c.id === "GICS" ? 'tier:"INDUSTRY"' : ""}){ id name }`)
          .join(" ")
          .value()
        return {
          id: `securityDescriptors-${modelId}${ticker}${exchange}${latestDate}`,
          query: `query ($modelId: String!, $ticker: String, $mic: String, $on: Date!) {
            model (id: $modelId) {
              security (ticker: $ticker, mic: $mic) {
                descriptors(on: $on) {
                  name
                  ticker
                  mic
                  currency
                  country
                  assetClass
                  assetSubclass
                  modelProviderId
                  internalId
                }
                ${classificationsQueries}
              }
            }
          }`,
          variables: {
            modelId,
            ticker,
            mic: exchange,
            on: latestDate,
          },
        }
      }

      return null
    },
  }, {
    resource: "etfSecurityDescriptors",
    url: ({ account, modelId, security: { exchange, ticker }, latestDate }) => {
      if (!exchange || !ticker || !latestDate) {
        return null
      }

      return {
        id: `etfSecurityDescriptors-${account}-${modelId}-${latestDate}`,
        url: `/api/${account}/m/${modelId}/securities/descriptors?date=${latestDate}`,
        payload: { ids: [`${ticker}.${exchange}`] },
      }
    },
  }, {
    resource: "baseSecurityDescriptors",
    url: ({ modelId, base, latestDate }) => {
      if (base && base.type === "security" && latestDate) {
        return {
          id: `baseSecurityDescriptors-${modelId}${base.ticker}${base.mic}${latestDate}`,
          query: `query ($modelId: String!, $ticker: String, $mic: String, $on: Date!) {
            model (id: $modelId) {
              security (ticker: $ticker, mic: $mic) {
                descriptors(on: $on) {
                  name
                  ticker
                  mic
                  currency
                  country
                  assetClass
                  modelProviderId
                }
              }
            }
          }`,
          variables: {
            modelId,
            ticker: base.ticker,
            mic: base.mic,
            on: latestDate,
          },
        }
      }

      return null
    },
  }, {
    resource: "securityWatchlists",
    as: null,
    url: ({ security, account, modelId, latestDate }) => {
      if (latestDate) {
        return `/api/${account}/m/${modelId}/watchlists?security=${security.ticker}.${security.exchange}&date=${latestDate}`
      }

      return null
    },
  }, {
    resource: "benchmarksAndPortfolios",
    as: null,
    url: ({ modelId }) => {
      if (modelId) {
        return ({
          id: `benchmarksAndPortfolios-${modelId}`,
          query: `query ($modelId: String!) {
            model (id: $modelId) {
              benchmarks { id, name, availableFrom }
            }
            portfolios { id, availableFrom }
          }`,
          variables: { modelId },
        })
      } else {
        return null
      }
    },
  }, {
    ...CONTENT_SETS_METADATA_QUERY,
    as: null,
  }, {
    resource: "classifications",
    as: null,
    url: () => ({
      id: "classifications",
      query: classificationsQuery,
    }),
  }, {
    ...FORECAST_LIST_QUERY,
    as: null,
  }, {
    resource: "basketResource",
    url: ({ security }) => {
      const { exchange, ticker } = security || {}
      if (exchange !== "_SWP") { return null }
      return {
        query: `query($resourceType: ResourceType!, $id: IdOrShortIdOrAlias!) {
          resource(type: $resourceType, id: $id) {
            id
            ... on SwapResource {
              currencyOverride
            }
          }
        }`,
        variables: {
          resourceType: "SWAP",
          id: ticker,
        },
      }
    },
  }, MODEL_METADATA_QUERY],
  mapStateToProps: (state, props) => ({
    ...state.modules.securityProfile,
    period: getProfilePeriod(state, props),
    earliestDate: getEarliestDate(state, props),
    latestDate: getLatestDate(state, props),
    date: getSelectedDate(state, props),
    securityWatchlists: getSecurityWatchlists(state),
    security: getSecurity(state, props),
    rawSecurityId: getRawSecurityId(state, props),
    watchlistsMenuVisible: getWatchlistsMenuVisible(state),
    base: getBase(state),
    baseName: getBaseName(state),
    datesAdjusted: getDatesAdjusted(state, props),
    fileName: getFileName(state, props),
    missingDataFiles: getMissingDataFiles(state),
    menuItems: getMenuItems(state, props),
    basketUUID: get(props, "basketResource.resource.id"),
    displayCompositeLink: getDisplayCompositeLink(state, props),
    analyzeCompositeAvailable: getAnalyzeCompositeAvailable(state, props),
    currency: getCurrency(state),
    unsupportedCurrencyWarning: getUnsupportedCurrencyWarning(state),
    isIntradayAnalyticsEnabled: getIsIntradayAnalyticsEnabled(state),
  }),
  actions: {
    setDate: PortfolioRootCreators.setProfileDate,
    resetDate: PortfolioRootCreators.resetProfileDate,
    setPeriod: PortfolioRootCreators.setProfilePeriod,
    addNotification: notificationsCreators.add,
    ...Creators,
  },
})
